import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  DialogContentText
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_ENDPOINTS } from "../../../../config/api";

const PermissionsList = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    guard_name: "web"
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    permissionId: null
  });

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching from:', API_ENDPOINTS.PERMISSIONS.BASE);
      
      const token = localStorage.getItem('token'); // Add if using auth
      const response = await axios.get(API_ENDPOINTS.PERMISSIONS.BASE, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
    
      
      const permissionsData = Array.isArray(response.data) ? response.data : response.data.data || [];
      setPermissions(permissionsData);
      console.log('Processed permissions:', permissionsData);
    } catch (error) {
      console.error("Full error:", error);
      console.error("Error response:", error.response);
      setError(error.response?.data?.message || "Failed to fetch permissions");
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (permission = null) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({
        name: permission.name,
        guard_name: permission.guard_name
      });
    } else {
      setEditingPermission(null);
      setFormData({
        name: "",
        guard_name: "web"
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPermission(null);
    setFormData({
      name: "",
      guard_name: "web"
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const config = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };
      
      let response;
      if (editingPermission) {
        response = await axios.put(
          `${API_ENDPOINTS.PERMISSIONS.BASE}/${editingPermission.id}`,
          formData,
          config
        );
      } else {
        response = await axios.post(
          API_ENDPOINTS.PERMISSIONS.BASE,
          formData,
          config
        );
      }
      
      console.log('Server response:', response.data);
      handleCloseDialog();
      fetchPermissions();
      if (!editingPermission) {
        setSnackbar({
          open: true,
          message: 'Permission added successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Permission updated successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error("Error saving permission:", error);
      const errorMessage = error.response?.data?.message || 
                         error.message ||
                         "Failed to save permission";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`${API_ENDPOINTS.PERMISSIONS.BASE}/${id}`);
      fetchPermissions();
      setSnackbar({
        open: true,
        message: 'Permission deleted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error deleting permission:", error);
      setError(error.response?.data?.message || "Error deleting permission");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      permissionId: null
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.permissionId) {
      handleDelete(deleteDialog.permissionId);
      handleDeleteCancel();
    }
  };

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Permission
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : permissions.length === 0 ? (
        <Typography variant="body1" align="center" sx={{ p: 3 }}>
          No permissions found
        </Typography>
      ) : (
        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="Permissions Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Guard Name</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((permission,index) => (
                  <TableRow key={permission.id}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{permission.name}</TableCell>
                    <TableCell>{permission.guard_name}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(permission)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, permissionId: permission.id })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={permissions.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {editingPermission ? "Edit Permission" : "Add New Permission"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Permission Name"
              type="text"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              name="guard_name"
              label="Guard Name"
              type="text"
              fullWidth
              value={formData.guard_name}
              onChange={handleChange}
              required
              disabled
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 
                (editingPermission ? "Update" : "Create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this permission?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PermissionsList;